import { Injectable } from '@angular/core';
import { AngularFirestore, AngularFirestoreCollection, AngularFirestoreDocument } from 'angularfire2/firestore';
import { Observable } from 'rxjs/Observable';
import { Globals } from '../globals';
import * as firebase from 'firebase/app';

interface AssistanceReport {
    active: boolean;
    captureUser?: string;
    createdAt: any;
    generationDate: any;
    reportDate: any;
    status: number;
    updatedAt: any;
}

interface AssistanceReportId extends AssistanceReport {
    id: string;
}

interface EmployeeReport {
    carrierId: any;
    carrierEmployee: any;
    carrierStatus: number;
    carrierValue: string;
    checkClockId: any;
    checkClockStatus: number;
    checkClockValue: string;
    createdAt: any;
    employeeId: string;
    employeeDepartmentId?: string;
    employeeName: string;
    employeeFolio: string;
    prompterId: any;
    prompterStatus: number;
    prompterValue: string;
    supervisorId: any;
    supervisorStatus: number;
    supervisorValue: string;
    supervisorActivity?: string;
    supervisorAdvance?: number;
    status: string;
    updatedAt: any;
}

interface EmployeeReportId extends EmployeeReport {
    id: string;
}

@Injectable()
export class AssistanceReportService {

    assistanceReportCollection: AngularFirestoreCollection<AssistanceReport>;
    assistanceReports: any;

    assistanceReportDocument: AngularFirestoreDocument<AssistanceReportId>;
    assistanceReport: Observable<any>;

    employeeReportCollection: any;
    employeesReport: any;

    collection: string;

    constructor(private afs: AngularFirestore) {
        this.collection = Globals.ASSISTANCE_REPORT_COLLECTION;
    }

    postAssistanceReport(assistanceReportId: string, assistanceReportObject: any) {
        this.assistanceReportCollection = this.afs.collection(this.collection);

        return this.assistanceReportCollection.doc(assistanceReportId).set(assistanceReportObject).then(function() {
            // console.log('Document added with ID: ', docRef.id);
            return assistanceReportId;
        }).catch(function(error) {
            // console.error('Error adding document: ', error);
            return '';
        });
    }

    getAssistanceReport(assistanceReportId: string) {
        this.assistanceReportDocument = this.afs.doc(this.collection + '/' + assistanceReportId);

        this.assistanceReport = this.assistanceReportDocument.valueChanges();

        return this.assistanceReport.map(object => object);
    }

    getAssistanceReportEmployees(assistanceReportId: string) {
        this.assistanceReportDocument = this.afs.doc(this.collection + '/' + assistanceReportId);

        this.assistanceReport = this.assistanceReportDocument.snapshotChanges().map(action => {
            const id$ = action.payload.id;

            const data = action.payload.data() as AssistanceReport;

            return { id$, ...data };
        }).map(document => {
            this.employeeReportCollection = this.afs.doc(this.collection + '/' + document.id$).collection('employees');

            const employeesRef: any = this.employeeReportCollection.snapshotChanges().map(actions => {
                return actions.map(a => {
                    const id$ = a.payload.doc.id;

                    const data = a.payload.doc.data() as EmployeeReport;

                    return { id$, ...data };
                });
            });

            return Observable.combineLatest(employeesRef, (employeesData) => {
                return {
                    employees: employeesData
                };
            }).map(data => {
                return {
                    employees: data.employees,
                    ...document
                };
            });
        }).mergeMap(object => Observable.combineLatest(object));

        return this.assistanceReport;
    }

    postAssistanceReportEmployeesAtomic(assistanceReportId: string, employees: any) {
        const batch = firebase.firestore().batch();

        this.employeeReportCollection = firebase.firestore().collection(this.collection).doc(assistanceReportId).collection('employees');

        for (let item in employees) {
            const employeeRef = this.employeeReportCollection.doc(employees[item].employeeId);

            batch.set(employeeRef, employees[item]);
        }

        return batch.commit();
    }

    putAssistanceReportEmployee(assistanceReportId: string, assistanceReportEmpoyeeObject: any, assistanceReportEmployeeId: string) {
        this.employeeReportCollection = this.afs.collection(this.collection).doc(assistanceReportId).collection('employees');

        return this.employeeReportCollection.doc(assistanceReportEmployeeId).update(assistanceReportEmpoyeeObject).then(function() {
            // console.log('Document updated');
        }).catch(function(error) {
            // console.error('Error updating document: ', error);
        });
    }

    delAssistanceReport(assistanceReportId: string) {
        this.assistanceReportDocument = this.afs.doc(this.collection + '/' + assistanceReportId);

        return this.assistanceReportDocument.delete().then(function() {
            // console.log('Document deleted');
        }).catch(function(error) {
            // console.error('Error deleting document: ', error);
        });
    }

    getAssistanceReportsByPeriod(initDate, endDate) {
        this.assistanceReportCollection = this.afs.collection(this.collection, ref => ref.where('reportDate', '>=', initDate)
                                                                                         .where('reportDate', '<=', endDate));

        this.assistanceReports = this.assistanceReportCollection.snapshotChanges().map(actions => {
            return actions.map(a => {
                const id$ = a.payload.doc.id;

                return { id$ };
            });
        }).map(object => object);

        return this.assistanceReports;
    }

    getAssistanceReportEmployeesByPeriod(initDate, endDate) {
        this.assistanceReportCollection = this.afs.collection(this.collection, ref => ref.where('reportDate', '>=', initDate)
                                                                                         .where('reportDate', '<=', endDate));

        this.assistanceReports = this.assistanceReportCollection.snapshotChanges().map(actions => {
            return actions.map(a => {
                const id$ = a.payload.doc.id;

                const data = a.payload.doc.data() as AssistanceReport;

                return { id$, ...data };
            });
        }).map(documents => {
            return documents.map(document => {
                this.employeeReportCollection = this.afs.doc(this.collection + '/' + document.id$).collection('employees');

                const employeesRef: any = this.employeeReportCollection.snapshotChanges().map(actions => {
                    return actions.map(a => {
                        const id$ = a.payload.doc.id;

                        const data = a.payload.doc.data() as EmployeeReport;

                        return { id$, ...data };
                    });
                });

                return Observable.combineLatest(employeesRef, (employeesData) => {
                    return {
                        employees: employeesData
                    };
                }).map(data => {
                    return {
                        employees: data.employees,
                        ...document
                    };
                });
            });
        }).mergeMap(object => {
            return object.length === 0 ? Observable.of([]) : Observable.combineLatest(object);
        });

        return this.assistanceReports;
    }

    putAssistanceReportAtomic(assistanceReports: any) {
        const batch = firebase.firestore().batch();

        this.employeeReportCollection = firebase.firestore().collection(this.collection);
        const currentDate = new Date();

        for (let item in assistanceReports) {
            const assistanceReportRef = this.employeeReportCollection.doc(assistanceReports[item]);

            batch.update(assistanceReportRef, {status: 1, updatedAt: currentDate });
        }

        return batch.commit();
    }

}
